import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import reverseDeletedShipmentQuantities from "../../../function/utilityAPI/reverseDeletedShipmentQuantities";
import checkPOOpenQuantities from "../../../function/utilityAPI/checkPOOpenQuantities";

export default function ReverseShipmentQuantities() {
  const client = useApolloClient();
  const [csvText, setCsvText] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [checkResults, setCheckResults] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      throw new Error("CSV must have at least a header and one data row");
    }

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const codeIndex = headers.findIndex((h) => h.includes("code"));
    const poIndex = headers.findIndex((h) => h.includes("po") && h.includes("number"));
    const qtyIndex = headers.findIndex((h) => h.includes("quantity") || h.includes("qty"));

    if (codeIndex === -1 || poIndex === -1 || qtyIndex === -1) {
      throw new Error("CSV must have columns: code, po number, and quantity");
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      if (values.length > 0 && values[codeIndex] && values[poIndex] && values[qtyIndex]) {
        data.push({
          code: values[codeIndex],
          poNumber: values[poIndex],
          quantity: values[qtyIndex],
        });
      }
    }

    return data;
  };

  const handleProcess = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const csvData = parseCSV(csvText);
      if (csvData.length === 0) {
        throw new Error("No valid data rows found in CSV");
      }

      const result = await reverseDeletedShipmentQuantities({
        client,
        csvData,
      });

      setResults(result);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckQuantities = async () => {
    setCheckLoading(true);
    setError(null);
    setCheckResults(null);

    try {
      const csvData = parseCSV(csvText);
      if (csvData.length === 0) {
        throw new Error("No valid data rows found in CSV");
      }

      const result = await checkPOOpenQuantities({
        client,
        csvData,
      });

      setCheckResults(result);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setCheckLoading(false);
    }
  };

  const handleLoadExample = () => {
    const example = `date,code,po number,Quantity
1/16/26,96539941,4519720807,450000
1/16/26,20103729,4519820527,40000`;
    setCsvText(example);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack spacing={3}>
        <Typography variant="h4">Reverse Deleted Shipment Quantities</Typography>
        <Typography variant="body2" color="text.secondary">
          This utility reverses quantities for deleted shipments. Paste CSV data with columns:
          code, po number, and Quantity. The PO number will be formatted as A6P-[po number from CSV]-00010.
        </Typography>

        <TextField
          label="CSV Data"
          multiline
          rows={10}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          fullWidth
          placeholder="Paste CSV data here..."
        />

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleCheckQuantities}
            disabled={checkLoading || !csvText.trim()}
            color="info"
          >
            {checkLoading ? <CircularProgress size={20} /> : "Check Quantities"}
          </Button>
          <Button
            variant="contained"
            onClick={handleProcess}
            disabled={loading || !csvText.trim()}
          >
            {loading ? <CircularProgress size={20} /> : "Process"}
          </Button>
          <Button variant="outlined" onClick={handleLoadExample}>
            Load Example
          </Button>
        </Stack>

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {checkResults && (
          <Stack spacing={2}>
            <Alert severity="info">
              Checked: {checkResults.length} POs | 
              Found: {checkResults.filter(r => r.status === "found").length} | 
              Not Found: {checkResults.filter(r => r.status === "not_found").length} | 
              Errors: {checkResults.filter(r => r.status === "error").length}
            </Alert>

            <Paper>
              <Typography variant="h6" sx={{ p: 2 }}>PO Open Quantities Report</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Deleted Qty</TableCell>
                      <TableCell>Original Qty</TableCell>
                      <TableCell>Current OpenQty</TableCell>
                      <TableCell>Difference</TableCell>
                      <TableCell>Needs Reversal</TableCell>
                      <TableCell>Already Reversed</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {checkResults.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.poNumber}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.originalQty ?? "N/A"}</TableCell>
                        <TableCell>{item.openQty ?? "N/A"}</TableCell>
                        <TableCell>{item.difference ?? "N/A"}</TableCell>
                        <TableCell>
                          {item.needsReversal !== undefined 
                            ? (item.needsReversal ? "Yes" : "No")
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.alreadyReversed !== undefined 
                            ? (item.alreadyReversed ? "Yes" : "No")
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.status === "found" && "Found"}
                          {item.status === "not_found" && "Not Found"}
                          {item.status === "error" && `Error: ${item.error}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        )}

        {results && (
          <Stack spacing={2}>
            <Alert severity="info">
              Processed: {results.processed} | Reversed: {results.reversed} | 
              Not Found: {results.notFound.length} | 
              Already Reversed: {results.alreadyReversed.length} | 
              Errors: {results.errors.length}
            </Alert>

            {results.notFound.length > 0 && (
              <Paper>
                <Typography variant="h6" sx={{ p: 2 }}>Not Found ({results.notFound.length})</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>PO Number</TableCell>
                        <TableCell>Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.notFound.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.code}</TableCell>
                          <TableCell>{item.poNumber}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {results.alreadyReversed.length > 0 && (
              <Paper>
                <Typography variant="h6" sx={{ p: 2 }}>Already Reversed ({results.alreadyReversed.length})</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>PO Number</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Current OpenQty</TableCell>
                        <TableCell>Original Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.alreadyReversed.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.code}</TableCell>
                          <TableCell>{item.poNumber}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.currentOpenQty}</TableCell>
                          <TableCell>{item.originalQty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {results.errors.length > 0 && (
              <Paper>
                <Typography variant="h6" sx={{ p: 2 }}>Errors ({results.errors.length})</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.errors.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{JSON.stringify(item.row)}</TableCell>
                          <TableCell>{item.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
